import type { MutableRefObject } from 'react';
import React, { useCallback, useEffect, useState } from 'react';
import { Checkbox } from '@this/src/components/shared/ui/inputs/checkbox';
import { Radio } from '@this/src/components/shared/ui/inputs/radio';
import { Select } from '@this/src/components/shared/ui/inputs/select';
import DatetimePicker from '@this/shared/datetime_picker/datetime_picker';
import { Box } from '@material-ui/core';
import moment from 'moment';
import type { TicketingMethodKey2 } from '@this/src/domain/arrangement/ticketing_instruction';
import { TicketingMethod2, TicketingMethodKeys2 } from '@this/src/domain/arrangement/ticketing_instruction';
import type { PreferredTicketingMethodQueryKey } from '@this/src/domain/arrangement/preferred_ticketing_method_query';
import {
  PreferredTicketingMethodQuery,
  PreferredTicketingMethodQueryKeys
} from '@this/src/domain/arrangement/preferred_ticketing_method_query';
import { SearchArea, SearchBlock, SearchLabel } from '../search_area';
import useOrganizationSelector from '../use_organization_selector';

interface Props {
  queryRef: MutableRefObject<URLSearchParams | null>;
  page: number;
  onFetch: () => void;
}

type SortKey = 'organization' | 'received_at' | 'started_at';
type SortDirection = 'asc' | 'desc';

interface State {
  preferredTicketingMethod: PreferredTicketingMethodQueryKey;
  ticketingMethod: TicketingMethodKey2;
  onlyPremiumSupport: boolean;
  sortKey: SortKey;
  sortDirection: SortDirection;
  startDateFrom: string | null;
  startDateTo: string | null;
  receivedDatetimeFrom: string | null;
  receivedDatetimeTo: string | null;
}

const initialState: () => State = () => ({
  preferredTicketingMethod:
    (utils.getParam('preferred_ticketing_method') as PreferredTicketingMethodQueryKey) || 'all',
  ticketingMethod: (utils.getParam('ticketing_method') as TicketingMethodKey2) || 'all',
  onlyPremiumSupport: utils.getParam('only_premium_support') === 'true',
  sortKey: (utils.getParam('sort_key') as SortKey) || 'organization',
  sortDirection: (utils.getParam('sort_direction') as SortDirection) || 'asc',
  startDateFrom: utils.getParam('start_date_from') || null,
  startDateTo: utils.getParam('start_date_to') || null,
  receivedDatetimeFrom: utils.getParam('received_datetime_from') || null,
  receivedDatetimeTo: utils.getParam('received_datetime_to') || null
});

const TicketingInstructionSearchArea: React.FC<Props> = ({ queryRef, page, onFetch }) => {
  const [organizationIds, renderOrganizationSelector] = useOrganizationSelector();
  const [state, setState] = useState<State>(initialState());
  const {
    preferredTicketingMethod,
    ticketingMethod,
    onlyPremiumSupport,
    sortKey,
    sortDirection,
    startDateFrom,
    startDateTo,
    receivedDatetimeFrom,
    receivedDatetimeTo
  } = state;

  const ticketingInstructionQueryResetEventName = 'ticketing-instruction-query-reset';
  const dateFormat = 'YYYY-MM-DD';
  const dateTimeFormat = 'YYYY-MM-DD HH:mm:ss';

  useEffect(() => {
    if (queryRef.current === null) {
      queryRef.current = new URLSearchParams();
    }
    queryRef.current.set('organization_ids', organizationIds.join(','));
    queryRef.current.set('preferred_ticketing_method', state.preferredTicketingMethod);
    queryRef.current.set('ticketing_method', state.ticketingMethod);
    queryRef.current.set('only_premium_support', state.onlyPremiumSupport.toString());
    queryRef.current.set('sort_key', state.sortKey);
    queryRef.current.set('sort_direction', state.sortDirection);
    queryRef.current.set('page', page.toString());

    if (state.startDateFrom) {
      queryRef.current.set('start_date_from', state.startDateFrom);
    } else {
      queryRef.current.delete('start_date_from');
    }

    if (state.startDateTo) {
      queryRef.current.set('start_date_to', state.startDateTo);
    } else {
      queryRef.current.delete('start_date_to');
    }

    if (state.receivedDatetimeFrom) {
      queryRef.current.set('received_datetime_from', state.receivedDatetimeFrom);
    } else {
      queryRef.current.delete('received_datetime_from');
    }

    if (state.receivedDatetimeTo) {
      queryRef.current.set('received_datetime_to', state.receivedDatetimeTo);
    } else {
      queryRef.current.delete('received_datetime_to');
    }

    onFetch();
  }, [page, organizationIds, state, onFetch, queryRef]);

  useEffect(() => {
    const handleQueryReset = (event: CustomEvent) => {
      const {
        startDateFrom: newStartDateFrom,
        startDateTo: newStartDateTo,
        receivedDatetimeFrom: newReceivedDatetimeFrom,
        receivedDatetimeTo: newReceivedDatetimeTo
      } = event.detail;

      setState(prevState => ({
        ...prevState,
        startDateFrom: newStartDateFrom,
        startDateTo: newStartDateTo,
        receivedDatetimeFrom: newReceivedDatetimeFrom,
        receivedDatetimeTo: newReceivedDatetimeTo
      }));
    };

    window.addEventListener(ticketingInstructionQueryResetEventName, handleQueryReset as EventListener);

    return () => {
      window.removeEventListener(ticketingInstructionQueryResetEventName, handleQueryReset as EventListener);
    };
  }, []);

  const handlePreferredTicketingMethodChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setState(state => ({
      ...state,
      preferredTicketingMethod: e.target.value as PreferredTicketingMethodQueryKey
    }));
    e.persist();
  }, []);

  const handleTicketingMethodChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setState(state => ({ ...state, ticketingMethod: e.target.value as TicketingMethodKey2 }));
    e.persist();
  }, []);

  const handleOnlyPremiumSupportChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setState(state => ({ ...state, onlyPremiumSupport: e.target.checked }));
    e.persist();
  }, []);

  const handleSortKeyChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setState(state => ({ ...state, sortKey: e.target.value as SortKey }));
    e.persist();
  }, []);

  const handleSortAscChange = useCallback((e: React.ChangeEvent<Record<string, unknown>>) => {
    setState(state => ({ ...state, sortDirection: 'asc' }));
    e.persist();
  }, []);

  const handleSortDescChange = useCallback((e: React.ChangeEvent<Record<string, unknown>>) => {
    setState(state => ({ ...state, sortDirection: 'desc' }));
    e.persist();
  }, []);

  const handleStartDateFromChange = useCallback((date: moment.Moment | undefined) => {
    const newFromDate = date ? date.format(dateFormat) : null;
    setState(prevState => ({
      ...prevState,
      startDateFrom: newFromDate
    }));
  }, []);

  const handleStartDateToChange = useCallback((date: moment.Moment | undefined) => {
    const newToDate = date ? date.format(dateFormat) : null;
    setState(prevState => ({
      ...prevState,
      startDateTo: newToDate
    }));
  }, []);

  const handleReceivedDatetimeFromChange = useCallback(
    (date: moment.Moment | undefined) => {
      const newFromDate = date ? date.second(0).format(dateTimeFormat) : null;
      setState(prevState => ({
        ...prevState,
        receivedDatetimeFrom: newFromDate
      }));
    },
    [dateTimeFormat]
  );

  const handleReceivedDatetimeToChange = useCallback(
    (date: moment.Moment | undefined) => {
      const newToDate = date ? date.second(59).format(dateTimeFormat) : null;
      setState(prevState => ({
        ...prevState,
        receivedDatetimeTo: newToDate
      }));
    },
    [dateTimeFormat]
  );

  return (
    <SearchArea>
      <SearchBlock>
        <SearchLabel>法人</SearchLabel>
        {renderOrganizationSelector()}
      </SearchBlock>
      <SearchBlock>
        <SearchLabel>旅程の発券方法</SearchLabel>
        <Select
          value={preferredTicketingMethod}
          onChange={handlePreferredTicketingMethodChange}
          style={{ marginBottom: 0, marginRight: '5px' }}
        >
          {PreferredTicketingMethodQueryKeys.map(key => (
            <option key={key} value={key}>
              {PreferredTicketingMethodQuery[key]}
            </option>
          ))}
        </Select>
      </SearchBlock>
      <SearchBlock>
        <SearchLabel>発券方法</SearchLabel>
        <Select
          value={ticketingMethod}
          onChange={handleTicketingMethodChange}
          style={{ marginBottom: 0, marginRight: '5px' }}
        >
          {TicketingMethodKeys2.map(key => (
            <option key={key} value={key}>
              {TicketingMethod2[key]}
            </option>
          ))}
        </Select>
      </SearchBlock>
      <SearchBlock>
        <Checkbox checked={onlyPremiumSupport} onChange={handleOnlyPremiumSupportChange}>
          <b style={{ fontSize: '12px' }}>土日祝オプション企業のみ</b>
        </Checkbox>
      </SearchBlock>
      <SearchBlock>
        <SearchLabel>出発日</SearchLabel>
        <Box display="flex" alignItems="center">
          <DatetimePicker
            value={startDateFrom ? moment(startDateFrom) : undefined}
            onChange={handleStartDateFromChange}
            onClear={() => handleStartDateFromChange(undefined)}
            showTime={false}
            disabledDays={0}
            showPast
            border
            placeholder="開始日"
          />
          <Box mx={1}>〜</Box>
          <DatetimePicker
            value={startDateTo ? moment(startDateTo) : undefined}
            onChange={handleStartDateToChange}
            onClear={() => handleStartDateToChange(undefined)}
            showTime={false}
            disabledDays={0}
            showPast
            border
            placeholder="終了日"
          />
        </Box>
      </SearchBlock>
      <SearchBlock>
        <SearchLabel>依頼日</SearchLabel>
        <Box display="flex" alignItems="center">
          <DatetimePicker
            value={receivedDatetimeFrom ? moment(receivedDatetimeFrom) : undefined}
            onChange={handleReceivedDatetimeFromChange}
            onClear={() => handleReceivedDatetimeFromChange(undefined)}
            showTime
            disabledDays={0}
            showPast
            border
            placeholder="開始日時"
          />
          <Box mx={1}>〜</Box>
          <DatetimePicker
            value={receivedDatetimeTo ? moment(receivedDatetimeTo) : undefined}
            onChange={handleReceivedDatetimeToChange}
            onClear={() => handleReceivedDatetimeToChange(undefined)}
            showTime
            disabledDays={0}
            showPast
            border
            placeholder="終了日時"
          />
        </Box>
      </SearchBlock>
      <SearchBlock>
        <SearchLabel>並び順</SearchLabel>
        <select value={sortKey} onChange={handleSortKeyChange} style={{ marginBottom: 0, marginRight: '5px' }}>
          <option value="received_at">依頼日</option>
          <option value="started_at">出発日</option>
        </select>
        <Radio checked={sortDirection === 'asc'} onChange={handleSortAscChange} style={{ marginRight: '5px' }}>
          昇順▲
        </Radio>
        <Radio checked={sortDirection === 'desc'} onChange={handleSortDescChange}>
          降順▼
        </Radio>
      </SearchBlock>
    </SearchArea>
  );
};

const propsAreEqual = (prevProps: Props, nextProps: Props) => {
  return prevProps.page === nextProps.page;
};

export default React.memo(TicketingInstructionSearchArea, propsAreEqual);
